import { firestore } from "@/services/firestore";
import {
  CustomerUpdate,
  CustomerUpdateStatus,
  CustomerUpdateTransition,
} from "@/types/v2/CustomerUpdate";
import { httpsCallables } from "@/repositories/httpsCallables";
import { db } from "@/services/db";
import {
  and,
  collection,
  CollectionReference,
  onSnapshot,
  or,
  query,
  where,
} from "firebase/firestore";

export const customerUpdate = {
  get: async (
    shopId: string,
    customerUpdateId: string
  ): Promise<CustomerUpdate | undefined> => {
    return firestore.getDocument<CustomerUpdate>(
      `shops/${shopId}/customerUpdates/${customerUpdateId}`
    );
  },
  subscribeAll: (
    id: string,
    callback: (doc: CustomerUpdate[] | undefined) => void
  ) => {
    return firestore.subscribeCollectionWQueries<CustomerUpdate>(
      `shops/${id}/customerUpdates`,
      callback,
      [
        {
          searchParam: "status",
          opsString: "==",
          equality: CustomerUpdateStatus.PENDING,
        },
        {
          searchParam: "status",
          opsString: "==",
          equality: CustomerUpdateStatus.CREATED,
        },
        {
          searchParam: "status",
          opsString: "==",
          equality: CustomerUpdateStatus.SCHEDULED,
        },
        {
          searchParam: "status",
          opsString: "==",
          equality: CustomerUpdateStatus.SNOOZED,
        },
      ]
    );
  },
  subscribeMine: (
    shopId: string,
    userId: string,
    callback: (doc: CustomerUpdate[] | undefined) => void
  ) => {
    const colRef = collection(
      db,
      `shops/${shopId}/customerUpdates`
    ) as CollectionReference<CustomerUpdate>;
    const colQuery = query(
      colRef,
      and(
        where("userId", "==", userId),
        or(
          where("status", "==", CustomerUpdateStatus.CREATED),
          where("status", "==", CustomerUpdateStatus.PENDING)
        )
      )
    );

    return onSnapshot(colQuery, (snapshot) => {
      const docs = snapshot.docs.map((doc) => doc.data());
      callback(docs);
    });
  },
  subscribeAllMine: (
    shopId: string,
    userId: string,
    callback: (doc: CustomerUpdate[] | undefined) => void
  ) => {
    const colRef = collection(
      db,
      `shops/${shopId}/customerUpdates`
    ) as CollectionReference<CustomerUpdate>;
    const colQuery = query(
      colRef,
      and(
        where("userId", "==", userId),
        or(
          where("status", "==", CustomerUpdateStatus.CREATED),
          where("status", "==", CustomerUpdateStatus.SCHEDULED),
          where("status", "==", CustomerUpdateStatus.PENDING),
          where("status", "==", CustomerUpdateStatus.SNOOZED)
        )
      )
    );

    return onSnapshot(colQuery, (snapshot) => {
      const docs = snapshot.docs.map((doc) => doc.data());
      callback(docs);
    });
  },
  scheduleUpdate: (
    shopId: string,
    customerUpdateId: string,
    desiredUpdateTime: Date,
    userId?: string
  ) => {
    return httpsCallables.updateCustomerUpdate({
      shopId,
      customerUpdateId,
      desiredUpdateTime,
      transition: CustomerUpdateTransition.CREATED_TO_SCHEDULED,
      userId,
    });
  },
  completeInitialUpdate: (
    shopId: string,
    cardId: string,
    customerUpdateId: string,
    desiredUpdateTime: Date,
    comments?: string,
    updateMedium?: CustomerUpdate["updateMedium"],
    userId?: string,
    skipNextUpdate?: boolean
  ) => {
    return httpsCallables.updateCustomerUpdate({
      shopId,
      customerUpdateId,
      comments,
      updateMedium,
      cardId,
      transition: CustomerUpdateTransition.CREATED_TO_COMPLETED,
      desiredUpdateTime,
      userId,
      skipNextUpdate,
    });
  },
  skipUpdate: (
    shopId: string,
    customerUpdateId: string,
    desiredUpdateTime: Date,
    comments?: string,
    userId?: string,
    skipNextUpdate?: boolean
  ) => {
    return httpsCallables.updateCustomerUpdate({
      shopId,
      customerUpdateId,
      comments,
      transition: CustomerUpdateTransition.PENDING_TO_SKIPPED,
      desiredUpdateTime,
      userId,
      skipNextUpdate,
    });
  },
  snoozeUpdate: (
    shopId: string,
    customerUpdateId: string,
    desiredUpdateTime: Date,
    userId?: string
  ) => {
    return httpsCallables.updateCustomerUpdate({
      shopId,
      customerUpdateId,
      desiredUpdateTime,
      userId,
      transition: CustomerUpdateTransition.PENDING_TO_SNOOZED,
    });
  },
  completeUpdate: (
    shopId: string,
    cardId: string,
    customerUpdateId: string,
    desiredUpdateTime: Date,
    comments?: string,
    updateMedium?: CustomerUpdate["updateMedium"],
    userId?: string
  ) => {
    return httpsCallables.updateCustomerUpdate({
      shopId,
      customerUpdateId,
      comments,
      updateMedium,
      cardId,
      transition: CustomerUpdateTransition.PENDING_TO_COMPLETED,
      desiredUpdateTime,
      userId,
    });
  },
  createUpdate: (
    shopId: string,
    cardId: string,
    serviceAdvisorId: string,
    desiredUpdateTime: Date
  ) => {
    return httpsCallables.updateCustomerUpdate({
      shopId,
      cardId,
      userId: serviceAdvisorId,
      desiredUpdateTime,
      transition: CustomerUpdateTransition.TO_CREATED,
    });
  },
  cancelUpdate: (
    shopId: string,
    customerUpdateId: string,
    comments?: string,
    updateMedium?: CustomerUpdate["updateMedium"]
  ) => {
    return httpsCallables.updateCustomerUpdate({
      shopId,
      customerUpdateId,
      comments,
      updateMedium,
      transition: CustomerUpdateTransition.PENDING_TO_CANCELLED,
    });
  },
  getAvailableUpdateTime: async (
    shopId: string,
    userId: string,
    desiredUpdateTime: Date
  ) => {
    const result = await httpsCallables.getAvailableUpdateTime({
      shopId,
      userId,
      desiredUpdateTime,
    });
    return { ...result, data: result.data ? new Date(result.data) : null };
  },
};
